import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from "react-hook-form";
import { SelectChangeEvent, Autocomplete, TextField, Tooltip, Input, InputAdornment, Select, MenuItem, Button, IconButton } from "@mui/material";
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Refresh } from "@mui/icons-material";
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchCustomers } from '../../store/customerStore/customerThunk';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { makeStyles } from "@material-ui/core/styles";
import { fetchAccountsByName } from "../../store/accountThunk";

import { StyledStack } from './Filters.styles';
import { size } from 'lodash';
import { SearchIcon } from 'lucide-react';
import { VocuherCategory, VocuherTypes, ReportTypes, Utils } from '../../utils/common-utils';

import { apiLimit } from "../../utils/api.types";

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});

interface FilterProps {
  type: string;
  filterHandler: (data: any) => void;
  selectedFilters?: (data: any) => void;
  setDefaultFilters?: any;
  hideFilter?: boolean ;
}

//filters component.
const Filters: FC<FilterProps> = ({ type, filterHandler, selectedFilters, setDefaultFilters, hideFilter }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);
  var date = new Date();
  // const [startDate, setStartDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth(), 1) : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [startDate, setStartDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth(), 1) : new Date());
  const [endDate, setEndDate] = useState<Date>((type === 'dashboard') ? new Date(date.getFullYear(), date.getMonth() + 1, 0) : new Date());

  const [selectedOption, setSelectedOption] = useState(String);
  const [filteroptions, setFilterOptions] = useState<IOptions[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  const [closedSelect, setClosedSelect] = useState(false);
  const [statusSelected, setStatusSelected] = useState("all");
  const [statusoptions, setStatusoptions] = useState<IOptions[]>([]);
  const [voucherOptions, setVoucherOptions] = useState<IOptions[]>([]);
  const [selectedDateRangeValue, setSelectedDateRangeValue] = useState("This FY Year");
  const { startDateFY, endDateFY } = Utils.getFinancialYearDates();
  const [dateRange, setDateRange] = useState({
    startDate: startDateFY,
    endDate: endDateFY
  });

  const [selectedVoucherType, setSelectedVoucherType] = useState(setDefaultFilters?.vType || 'all');
  const [showDateRange, setShowDateRange] = useState(false);

  // use form
  const methods = useForm<any>({});

  const formatDateToISO = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const createFilter = () => {
    let filter = (selectedOption && selectedOption !== 'all') ? `&filter=${selectedOption}` : '';
    let voucherType = (selectedVoucherType) ? `&voucherType=${selectedVoucherType}` : '';
    let status = (statusSelected && statusSelected !== 'all') ? `&status=${statusSelected}` : '';
    // Format dates to YYYY-MM-DD for server-side filtering
    const startDateStr = formatDateToISO(dateRange.startDate);
    const endDateStr = formatDateToISO(dateRange.endDate);
    let queryString = `start=${startDateStr}&end=${endDateStr}${filter}${voucherType}${status}`;
    filterHandler(queryString);

    let filterData = Object();
    if (selected && selectedFilters) { filterData.selectedClient = selected; }
    if (selectedFilters) { filterData.periodString = `Period: ${Utils.formatDate(dateRange.startDate)} - ${Utils.formatDate(dateRange.endDate)}`; }
    if (selectedFilters) { selectedFilters(filterData); }

  };

  const onResetClickHandler = () => {
    setSelectedOption('');
    setSelected(null);
    setNewOption('');
    setStatusSelected("all");
    setSelectedVoucherType(type == VocuherCategory.SALE ? VocuherTypes.GST_INVOICE : VocuherTypes.PURCHASE_INVOICE);
    setSelectedDateRangeValue('This FY Year');
    setDateRange({
      startDate: startDateFY,
      endDate: endDateFY
    });
    setShowDateRange(false);
  };

  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        const s = { ...dateRange };
        s.startDate = newValue;
        setDateRange(s);
      } else if (name === 'endDate') {
        const s = { ...dateRange };
        s.endDate = newValue;
        setDateRange(s);
      }
    }
  };

  //Fetch all the suppliers/Buyers list on page load.
  useEffect(() => {
    const getOptions = async () => {
      if (selectedAccount) {
        const { _id: id } = selectedAccount;
        const data: any = (type === 'account-payable') ? await dispatch(fetchSuppliers({ accountId: id, params: '' })) : await dispatch(fetchCustomers(id));

        if (data.payload.length > 0) {
          let option = [];
          option = data.payload.map((item: any) => {
            return { label: item.name, value: item._id }
          });
          option.push({ label: 'All', value: 'all' })
          setFilterOptions(option)
        }
      }
    }
    getOptions();
  }, [dispatch, selectedAccount, type]);

  useEffect(() => {
    if (dateRange.startDate && dateRange.startDate != undefined && dateRange.endDate && dateRange.endDate != undefined) {
      createFilter();
    }
  }, [dateRange, selectedVoucherType, selectedOption, statusSelected]);

  // Populate Status option on form load.
  useEffect(() => {
    let _statusOptions = [
      { label: 'All', value: 'all' },
      { label: "Draft", value: "DRAFT" },
      { label: "Pending", value: "PENDING" },
      { label: "Invoiced", value: "INVOICED" },
      { label: "Partially Paid", value: "PARTIALLY_PAID" },
      { label: "Paid", value: "PAID" },
      { label: "Over Paid", value: "OVER_PAID" },
      // { label: 'Invoice Sent', value: 'INVOICE_SENT' },
      { label: 'Outstanding', value: 'PENDING,PARTIALLY_PAID' }

    ]
    let _estimateStatusOptions = [
      { label: 'All', value: 'all' },
      { label: "Draft", value: "DRAFT" },
      { label: "Pending", value: "PENDING" },
      { label: "Accepted", value: "ACCEPTED" },
      { label: "Rejected", value: "REJECTED" },
    ]

    let _voucherTypeOptions;
    if (type == VocuherCategory.SALE) {
      _voucherTypeOptions = [
        { label: 'Select Voucher', value: 'all' },
        { label: VocuherTypes.GST_INVOICE, value: VocuherTypes.GST_INVOICE },
        { label: VocuherTypes.ESTIMATE, value: VocuherTypes.ESTIMATE },
        { label: VocuherTypes.CREDIT_NOTE, value: VocuherTypes.CREDIT_NOTE },
        { label: VocuherTypes.DELIVERY_NOTE, value: VocuherTypes.DELIVERY_NOTE },
        { label: VocuherTypes.PROFORMA_INVOICE, value: VocuherTypes.PROFORMA_INVOICE },
      ]
    } else {
      _voucherTypeOptions = [
        { label: 'All', value: 'all' },
        { label: VocuherTypes.PURCHASE_INVOICE, value: VocuherTypes.PURCHASE_INVOICE },
        { label: VocuherTypes.PURCHASE_ORDER, value: VocuherTypes.PURCHASE_ORDER },
      ]
    }
    setVoucherOptions(_voucherTypeOptions)

    if (type === 'sale-estimate') {
      setStatusoptions(_estimateStatusOptions);
    } else {
      setStatusoptions(_statusOptions);
    }

  }, []);

  const onChangeHandler = (event: any, value: any) => {
    setNewOption('');
    // setIsLoading(true);
    setSelectedOption(value._id);
    setSelected(value);
  }

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSelected('')
    setSelectedOption('');
    setNewOption(value);
    await dispatch(fetchAccountsByName(value)).unwrap().then((data) => {
      if (data) {
        // if(data.accounts.length > 0) {
        //   setOptions(data.accounts);
        // }
        setOptions(data.accounts);
      }
    });
  };

  const onCloseHandler = () => {
    setClosedSelect(true);
  };

  const onOpenHandler = () => {
    setClosedSelect(false);
  };

  const handleChangeVoucherType = (e: any) => {
    var _voucherTypeName = e.target.value;
    setSelectedVoucherType(_voucherTypeName);
  };

  const handleDateRange = (e: any) => {
    // console.log(e.target.value);
    var _dateFilterName = e.target.value;
    setSelectedDateRangeValue(_dateFilterName);
    // const _today = new Date();
    // const _quarter = Math.floor((_today.getMonth() / 3));
    // console.log(_quarter);

    // const startFullQuarter = new Date(today.getFullYear(), quarter * 3, 1);
    // const endFullQuarter = new Date(startFullQuarter.getFullYear(), startFullQuarter.getMonth() + 3, 0);

    if (e.target.value && e.target.value != '') {
      if (_dateFilterName === 'Today') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now()),
          endDate: new Date(Date.now())
        })

      } else if (_dateFilterName === 'Yesterday') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        });
      } else if (_dateFilterName === 'This Week') {
        setShowDateRange(false);
        var curr = new Date();
        setDateRange({
          // startDate: new Date(curr.setDate(curr.getDate() - curr.getDay())),
          // endDate: new Date(curr.setDate(curr.getDate() - curr.getDay()+6)),
          startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now())
        });
      } else if (_dateFilterName === 'Last Week') {
        setShowDateRange(false);
        setDateRange({
          startDate: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000),
          endDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        });
      } else if (_dateFilterName === 'This Month') {
        setShowDateRange(false);
        var date = new Date(), y = date.getFullYear(), m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m, 1),
          endDate: new Date(y, m + 1, 0)
        });
      } else if (_dateFilterName === 'Last Month') {
        setShowDateRange(false);
        var date = new Date(), y = date.getFullYear(), m = date.getMonth();
        setDateRange({
          startDate: new Date(y, m - 1, 1),
          endDate: new Date(y, (m - 1) + 1, 0)
        });
      } else if (_dateFilterName === 'FY Q1') {
        setShowDateRange(false);
        const _quarter = 1;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);

        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q2') {
        setShowDateRange(false);
        const _quarter = 2;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q3') {
        setShowDateRange(false);
        const _quarter = 3;
        const qStartDate = new Date(startDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 3, 0);
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'FY Q4') {
        setShowDateRange(false);
        const _quarter = 0;
        const qStartDate = new Date(endDateFY.getFullYear(), _quarter * 3, 1);
        const qEndDate = new Date(qStartDate.getFullYear(), qStartDate.getMonth() + 2, 31);
        // Date.today().clearTime().moveToFirstDayOfMonth();
        setDateRange({
          startDate: qStartDate,
          endDate: qEndDate
        });
      } else if (_dateFilterName === 'This FY Year') {
        setShowDateRange(false);
        setDateRange({
          startDate: startDateFY,
          endDate: endDateFY
        });
      } else if (_dateFilterName === 'Last FY Year') {
        setShowDateRange(false);
        // var date = new Date(), y = date.getFullYear() - 1, m = date.getMonth();
        const { startDateLastFY, endDateLastFY } = Utils.getLastFinancialYearDates();
        setDateRange({
          startDate: startDateLastFY,
          endDate: endDateLastFY
        });
      } else if (_dateFilterName === 'Custom') {
        setShowDateRange(true);
      } else {
        setShowDateRange(false);
        setSelectedDateRangeValue('This FY Year');
        setDateRange({
          startDate: startDateFY,
          endDate: endDateFY
        });
      }
    }
  };



  return (
    <div>
      {/* {JSON.stringify(filteroptions)} */}
      {/* <StyledStack> */}
      <IconButton onClick={() => { onResetClickHandler(); }}
        style={{ marginRight: '15px', marginLeft: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1', float: 'left' }}
        size='small' ><Refresh /></IconButton>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <FormProvider {...methods}>
          <>
            <Autocomplete
              disablePortal
              id={`Search Party}`}
              // className='c-text-field-3' 
              value={selected || null}
              style={{ width: 300, float: 'left' }}
              onChange={onChangeHandler}
              onInputChange={onInputChangeHandler}
              onClose={onCloseHandler}
              onOpen={onOpenHandler}
              options={options}
              classes={{ paper: classes.paper }}
              getOptionLabel={option => option.name}
              renderInput={(params) => <TextField {...params}
                // style={{width:300}} 
                className='c-text-field-3'
                variant="standard"
                name="search"
                size='small'
                placeholder={`Type here to search party ...`}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
              />}
            />
          </>

          {/* <TextField className='c-text-field-3' 
                variant="standard" 
                style={{width:300}}
                size='small'
                placeholder='Search Party, Invoice Number ...'
                InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start"><SearchIcon style={{marginTop:-3}} size={20}/></InputAdornment>,
                style:{ 
                  fontSize:14,
                  fontWeight: 'normal',
                },
                }}
                
              /> */}
          {/* {(type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && filteroptions.length > 0 && <CustomSelect
              name="selectOption"
              label={(type === VocuherCategory.PURCHASE) ? "Supplier" : "Buyer"}
              id="selectOption"
              variant="standard"
              options={filteroptions}
              value={selectedOption}
              onChange={(event: SelectChangeEvent<any>) => { setSelectedOption(event.target.value) }} />} */}
          {!hideFilter && (type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && statusoptions.length > 0 &&
            <>
              <Select
                name="status"
                disableUnderline
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                sx={{
                  boxShadow: "none",
                  width: 150,
                  ml: '10px !important',
                  pt: '0px !important',
                  pl: '10px !important',
                  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                  '& .MuiSelect-select': { p: 0 },
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                label="Statuses"
                className='c-text-field-3'
                id="status"
                variant="outlined"
                value={statusSelected}
                onChange={(event: SelectChangeEvent<any>) => { setStatusSelected(event.target.value); }} >
                {/* {showNoneLabel && <MenuItem value="">
                    <em>None</em>
                </MenuItem>} */}
                {statusoptions.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                    disabled={option.disable || false}
                  >
                    {option.label}
                  </MenuItem>
                ))}</Select>
            </>
          }


          <Select
            name="date"
            disableUnderline
            MenuProps={{
              PaperProps: {
                sx: {
                  border: "1.5px solid #e9ecf1",
                  boxShadow: 'none',
                  borderRadius: '5px'
                }
              }
            }}
            defaultValue={'all'}
            sx={{
              boxShadow: "none",
              width: 200,
              ml: '10px !important',
              pt: '0px !important',
              pl: '10px !important',
              '& .MuiOutlinedInput-input': { p: 0, m: 0 },
              '& .MuiSelect-select': { p: 0 },
              ".MuiOutlinedInput-notchedOutline": { border: 0 },
              "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
              "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
            }}
            label="Select Date"
            className='c-text-field-3'
            id="status"
            variant="outlined"
            value={selectedDateRangeValue || ''}
            onChange={(e) => { handleDateRange(e) }} >
            <MenuItem value={'all'} key={'all'}> Select Date</MenuItem>
            <MenuItem value={'Today'} key={'Today'}> Today </MenuItem>
            <MenuItem value={'Yesterday'} key={'Yesterday'}> Yesterday </MenuItem>
            <MenuItem value={'This Week'} key={'This Week'}> This Week </MenuItem>
            <MenuItem value={'Last Week'} key={'Last Week'}> Last Week </MenuItem>
            <MenuItem value={'This Month'} key={'This Month'}> This Month </MenuItem>
            <MenuItem value={'Last Month'} key={'Last Month'}> Last Month </MenuItem>
            <MenuItem value={'FY Q1'} key={'FY Q1'}> FY Q1 (Apr - Jun) </MenuItem>
            <MenuItem value={'FY Q2'} key={'FY Q2'}> FY Q2 (Jul - Sept) </MenuItem>
            <MenuItem value={'FY Q3'} key={'FY Q3'}> FY Q3 (Oct - Dec) </MenuItem>
            <MenuItem value={'FY Q4'} key={'FY Q4'}> FY Q4 (Jan - Mar) </MenuItem>
            <MenuItem value={'This FY Year'} key={'This FY Year'} selected> This FY Year (Apr - Mar) </MenuItem>
            <MenuItem value={'Last FY Year'} key={'Last FY Year'} > Last FY Year (Apr - Mar) </MenuItem>
            <MenuItem value={'Custom'} key={'Custom'}> Custom Date </MenuItem>
          </Select>

          {!hideFilter && (type === VocuherCategory.SALE || type === VocuherCategory.PURCHASE) && statusoptions.length > 0 &&
            <>

              <Select
                name="voucherType"
                disableUnderline
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                defaultValue={'all'}
                sx={{
                  boxShadow: "none",
                  width: 200,
                  ml: '10px !important',
                  pt: '0px !important',
                  pl: '10px !important',
                  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                  '& .MuiSelect-select': { p: 0 },
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                label="Statuses"
                className='c-text-field-3'
                id="status"
                variant="outlined"
                value={selectedVoucherType || ''}
                onChange={(e) => { handleChangeVoucherType(e) }} >
                {voucherOptions.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
                {/* { type == VocuherCategory.SALE &&
                  <>
                  <MenuItem value={'all'} key={'all'} > Select Voucher {selectedVoucherType}</MenuItem>
                  <MenuItem value={'Invoice'} key={'Invoice'} selected> Invoice </MenuItem>
                  <MenuItem value={'Credit Note'} key={'Credit Note'}> Credit Note </MenuItem>
                  <MenuItem value={'Quotation'} key={'Quotation'}> Quotation </MenuItem>
                  <MenuItem value={'Delivery Note'} key={'Delivery Note'}> Delivery Note </MenuItem>
                  <MenuItem value={'Proforma Invoice'} key={'Proforma Invoice'}>Proforma Invoice </MenuItem>
                  </>
                  }
                  { type == VocuherCategory.PURCHASE &&
                  <>
                  <MenuItem value={'all'} key={'all'} > Select Voucher</MenuItem>
                  <MenuItem value={'Purchase Order'} key={'Purchase Order'}> Purchase Order </MenuItem>
                  <MenuItem value={'Purchase Invoice'} key={'Purchase Invoice'} selected> Purchase Invoice </MenuItem>
                  </>
                  } */}
              </Select>
            </>
          }
          {showDateRange &&
            <>
              <span style={{ marginRight: 10 }} />
              <DesktopDatePicker
                inputFormat="dd/MM/yyyy"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: 14,
                    height: 28
                  },
                }}
                renderInput={(params) =>
                  <TextField {...params}
                    required
                    className='c-text-field-3'
                    variant="standard"
                    sx={{ width: 150 }}
                    size='small'
                    placeholder="Start Date"

                  />}
                value={dateRange.startDate || 0}
                onChange={(value) => { handleDateChange(value, 'startDate') }}
              />
              <span style={{ marginRight: 10 }} />
              <DesktopDatePicker
                inputFormat="dd/MM/yyyy"
                InputProps={{
                  disableUnderline: true,
                  style: {
                    fontSize: 14,
                    height: 28
                  },
                }}
                renderInput={(params) =>
                  <TextField {...params}
                    required
                    className='c-text-field-3'
                    variant="standard"
                    sx={{ width: 150 }}
                    size='small'
                    placeholder="End Date"

                  />}
                value={dateRange.endDate || 0}
                onChange={(value) => { handleDateChange(value, 'endDate') }}
              />
            </>
          }
        </FormProvider>
      </LocalizationProvider>
      {/* </StyledStack> */}
    </div>
  );
}

export default Filters;